<template>
	<div class="fixed inset-0 z-30 overflow-y-auto bg-black/40" @click="emit('close')">
		<div class="flex min-h-full items-center justify-center p-4">
			<div
				class="relative w-full max-w-lg p-5 bg-white rounded-lg"
				@click.stop
				@touchstart.passive="onTouchStart"
				@touchend.passive="onTouchEnd"
			>
				<header>
					<div class="mb-6 -mx-5 -mt-5 overflow-hidden aspect-video bg-neutral-50">
						<Image
							:url="buyable.image"
							:fallback="`${baseUrl}assets/headers/Hamamruimte_vriendinnen.webp`"
							:width="570"
							:height="360"
							:alt="buyable.name ?? 'Hamam ruimte'"
							class="object-cover object-center w-full h-full"
						/>
					</div>
					<button
						type="button"
						class="absolute inline-flex items-center justify-center w-10 h-10 text-white rounded-full right-5 top-5 bg-black/50 hover:bg-black/60"
						@click="emit('close')"
					>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-6">
							<path
								fill="currentColor"
								fill-rule="nonzero"
								d="M19.284 18.216a.76.76 0 0 1 0 1.068.769.769 0 0 1-1.068 0L12 13.06l-6.216 6.225a.769.769 0 0 1-1.068 0 .76.76 0 0 1 0-1.068L10.94 12 4.716 5.784a.76.76 0 0 1 1.068-1.068L12 10.94l6.216-6.225a.76.76 0 0 1 1.068 1.068L13.06 12l6.225 6.216Z"
							/>
						</svg>
					</button>

					<template v-if="hasMultipleOptions">
						<div class="relative flex items-center justify-center mb-4">
							<span class="absolute left-0 rounded-full bg-gold-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700">
								{{ activeIndex + 1 }} / {{ choices.length }}
							</span>
							<p class="px-14 text-sm text-center text-neutral-500">
								{{ t('choose_from_options', { count: choices.length }) }}
							</p>
						</div>

						<div class="flex items-center justify-between gap-3">
							<button
								type="button"
								class="flex items-center justify-center flex-none w-8 h-8 text-white rounded-full bg-accent"
								:aria-label="t('previous_option')"
								@click="previous"
							>
								<IconChevronLeft class="h-3 flex-none" />
							</button>
							<Transition name="fade" mode="out-in">
								<h3 :key="`title-${activeIndex}`" class="flex-1 font-serif text-2xl text-center">{{ title }}</h3>
							</Transition>
							<button
								type="button"
								class="flex items-center justify-center flex-none w-8 h-8 text-white rounded-full bg-accent"
								:aria-label="t('next_option')"
								@click="next"
							>
								<IconChevronRight class="h-3 flex-none" />
							</button>
						</div>

						<div class="flex items-center justify-center gap-1.5 mt-3">
							<button
								v-for="(dotChoice, index) in choices"
								:key="`dot_${dotChoice.id}`"
								type="button"
								class="rounded-full size-2 transition-colors"
								:class="index === activeIndex ? 'bg-accent' : 'bg-neutral-300'"
								:aria-label="`${index + 1} / ${choices.length}`"
								:aria-current="index === activeIndex ? 'true' : undefined"
								@click="activeIndex = index"
							/>
						</div>
					</template>

					<h3 v-else class="font-serif text-2xl">{{ title }}</h3>
				</header>

				<main
					class="my-6 text-neutral-600"
					:class="[
        '[&_p]:my-4',
        '[&_li>p]:inline [&_li>p]:my-0',
        '[&_a]:underline',
        '[&_ul]:list-disc [&_ul]:list-inside',
        '[&_ol]:list-decimal [&_ol]:list-inside',
      ]"
				>
					<Transition name="fade" mode="out-in">
						<div :key="`body-${activeIndex}`">
							<div v-if="description" v-html="description"></div>
							<ul v-if="usps.length" class="flex flex-col gap-1">
								<li v-for="(usp, index) in usps" :key="`usp_${index}`" class="flex items-start gap-1">
									<!-- The bullet glyph sits right of center in its viewBox; pull it left so the
										text sits close to the glyph, per design -->
									<SvgIcon name="icon-bullet-point" class="size-4 mt-0.5 flex-none text-neutral-950 -ml-2" />
									<!-- eslint-disable-next-line vue/no-v-html -- description is managed rich text from the DMP -->
									<span class="[&_p]:inline" v-html="usp.description" />
								</li>
							</ul>
						</div>
					</Transition>
				</main>

				<footer class="flex flex-col gap-3">
					<Transition name="fade" mode="out-in">
						<div v-if="priceData" :key="`price-${activeIndex}`" class="mb-2">
							<PriceLine :price-data="priceData" :selected-date="selectedDate ?? 'none'" />
						</div>
					</Transition>

					<button type="button" class="btn-primary h-10 w-full" @click="emit('book', id)">
						{{ t('book') }}
					</button>
					<a
						v-if="product_page_link_url"
						:href="product_page_link_url"
						class="text-xs font-medium underline underline-offset-2 text-neutral-950 self-center"
						target="_blank"
						rel="noopener noreferrer"
					>
						{{ t('view_page') }}
					</a>
				</footer>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import IconChevronLeft from '@thermenresorts/icons/svg/chevron-left.svg';
import IconChevronRight from '@thermenresorts/icons/svg/chevron-right.svg';
import type { Buyable, BuyableChoice } from '../api/resources';
import Image from "./Image.vue";
import PriceLine from "../components/PriceLine.vue";
import SvgIcon from "./SvgIcon.vue";

const { t } = useI18n();
const baseUrl = import.meta.env.BASE_URL;

const props = defineProps<{
  selectedDate?: string
  buyable: Buyable
  choice?: BuyableChoice | null
  choices?: BuyableChoice[]
  initialIndex?: number
}>();

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'book', id: number): void
}>();

const activeIndex = ref(props.initialIndex ?? 0);

const hasMultipleOptions = computed(() => (props.choices?.length ?? 0) > 1);

// Carousel wraps around: stepping past either end continues from the other side (#4693).
const previous = () => {
  const count = props.choices?.length ?? 0;
  if (count) activeIndex.value = (activeIndex.value - 1 + count) % count;
};
const next = () => {
  const count = props.choices?.length ?? 0;
  if (count) activeIndex.value = (activeIndex.value + 1) % count;
};

// Swipe left/right on touch devices browses the carousel; mostly-vertical
// or short swipes are ignored so scrolling keeps working as expected.
const touchStart = ref<{ id: number | null, x: number, y: number }>({ id: null, x: 0, y: 0 });

const onTouchStart = (event: TouchEvent) => {
  // A second finger means a pinch/multi-touch gesture, not a swipe: cancel tracking
  if (event.touches.length > 1) {
    touchStart.value = { id: null, x: 0, y: 0 };
    return;
  }

  const touch = event.changedTouches[0];
  touchStart.value = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
};

const onTouchEnd = (event: TouchEvent) => {
  if (!hasMultipleOptions.value) return;

  // Only accept the same finger that started the gesture
  const touch = Array.from(event.changedTouches).find(({ identifier }) => identifier === touchStart.value.id);
  if (!touch) return;

  const deltaX = touch.clientX - touchStart.value.x;
  const deltaY = touch.clientY - touchStart.value.y;

  if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;

  if (deltaX < 0) {
    next();
  } else {
    previous();
  }
};

// Carousel mode uses the active choice; single mode falls back to the passed choice/buyable
const activeChoice = computed<BuyableChoice | null>(() => props.choices?.[activeIndex.value] ?? props.choice ?? null);

const id = computed(() => activeChoice.value ? activeChoice.value.id : props.buyable.id);
const title = computed(() => activeChoice.value?.name ?? props.buyable.name);
// The pop-up shows the dedicated "meer informatie" USPs (empty when not configured in the DMP).
const usps = computed(() => {
  const source = activeChoice.value ? activeChoice.value.pre_reservation_info_usps : props.buyable.pre_reservation_info_usps;
  return (source ?? []).filter(usp => usp.description !== null);
});
const description = computed(() => activeChoice.value ? activeChoice.value.description : props.buyable.description);
const priceData = computed(() => activeChoice.value?.price_data ?? (props.choices ? null : props.buyable.price_data));
const product_page_link_url = computed(() => activeChoice.value ? activeChoice.value.product_page_link_url : props.buyable.product_page_link_url);
</script>

<style scoped>
/* Cross-fade the option content when browsing the carousel (#4693). */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
